import * as React from 'react';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
} from '@mui/material';

import HomeIcon from '@mui/icons-material/Home';
import PostAddOutlinedIcon from '@mui/icons-material/PostAddOutlined';

import useNavigateWithConfirm from '../core/hooks/useNavigateWithConfirm';
import { useAppData } from '../core/context/AppDataContext';

import FormDrawerListItem from './FormDrawerListItem';
import UserGuideDialog from './UserGuideDialog';
import useUploadUserGuide, { IFileMutationVariables } from '../core/api/hooks/common/useUploadUserGuide';
import useGetAdminAuthorizedForms from '../core/api/hooks/common/useGetAdminAuthorizedForms';

interface IFormDrawer {
  open: boolean;
  toggleOpen: () => void;
}

function FormDrawer({ open, toggleOpen }: IFormDrawer) {
  const navigateWithConfirm = useNavigateWithConfirm();
  const { authorizedForms, appUserInfo } = useAppData();
  const container = window !== undefined ? () => window.document.body : undefined;
  const [showUserGuideDialog, setShowUserGuideDialog] = React.useState(false);
  const { data: adminAuthorizedForms } = useGetAdminAuthorizedForms();
  const { mutate: uploadUserGuide } = useUploadUserGuide();

  const handleFormItemClick = (uri: string | null | undefined) => {
    if (uri) {
      navigateWithConfirm(uri);
    }
  };

  const handleExternalItemClick = (uri: string) => {
    window.open(uri, '_blank');
  };

  const handleFileUpload = (file: IFileMutationVariables | undefined) => {
    if (file) {
      uploadUserGuide(file, {
        onSuccess: () => {
          setShowUserGuideDialog(false);
        },
      });
    }
  };

  return (
    <Box component="nav">
      <UserGuideDialog
        isOpen={(showUserGuideDialog && adminAuthorizedForms && adminAuthorizedForms.length > 0) || false}
        onClose={() => setShowUserGuideDialog(false)}
        onAccept={(file) => handleFileUpload(file)}
        authorizedForms={adminAuthorizedForms ?? []}
      />
      <Drawer
        container={container}
        variant="temporary"
        open={open}
        onClose={toggleOpen}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 360 },
        }}
      >
        <Box
          onClick={toggleOpen}
          sx={{ pt: 1, textAlign: 'center', height: '100%' }}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
        >
          <Box>
            <IconButton size="large" onClick={() => handleFormItemClick('/')}>
              <HomeIcon />
            </IconButton>
            <Divider />
            <List>
              {authorizedForms?.map((authorizedForm) => (
                <FormDrawerListItem
                  key={authorizedForm.metadata?.id}
                  color={authorizedForm.metadata?.color}
                  title={authorizedForm.metadata?.reportType}
                  onClick={() => handleFormItemClick(authorizedForm.metadata?.route)}
                />
              ))}
              {appUserInfo?.roles && appUserInfo.roles.includes('dispatch-event-approve') && (
                <React.Fragment>
                  <Divider />
                  <FormDrawerListItem
                    isExternal
                    color="grey"
                    title="SOC Alert"
                    onClick={() => handleExternalItemClick('https://myairwis.com/SMS/DispatchNotification/Forms/Submitted.aspx')}
                  />
                </React.Fragment>
              )}
            </List>
          </Box>
          <Box>
            <Divider />
            <IconButton
              key="settings"
              onClick={() => setShowUserGuideDialog(true)}
            >
              <PostAddOutlinedIcon fontSize="large" />
            </IconButton>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}

export default FormDrawer;
